import React from 'react'
import { getShouldLoadProps } from './getShouldLoadProps'
import { addListener, removeListener } from './global-dom-events'
import { ShouldLoadProps } from './is-viewable'

interface ImpressionAvailableProps {
    available: () => void
    /** True when collection is still loading */
    loading: boolean
    /** 50 for element to be 50% visible in viewport */
    percentageVisibleThreshold?: number
    children: (ref: React.RefObject<any>) => React.ReactNode
}

export const ImpressionAvailable: React.FC<ImpressionAvailableProps> = ({
    available,
    loading,
    percentageVisibleThreshold = 50,
    children,
}) => {
    const lazyLoadRef = React.useRef<HTMLElement | null>(null)
    const impressionTriggered = React.useRef<boolean>()

    function shouldTrigger(shouldLoadProps: ShouldLoadProps) {
        return (
            !impressionTriggered.current &&
            !loading &&
            shouldLoadProps.percentVisible > percentageVisibleThreshold
        )
    }

    React.useEffect(() => {
        if (!lazyLoadRef.current) {
            console.debug(
                `innerRef passed by 'useLazyLoading' has not been added to an element`,
            )
            return
        }

        if (shouldTrigger(getShouldLoadProps(lazyLoadRef.current))) {
            impressionTriggered.current = true
            setTimeout(() => {
                available()
            })
        }

        function detectInViewport() {
            if (
                !lazyLoadRef.current ||
                !lazyLoadRef.current.getBoundingClientRect
            ) {
                return
            }

            const shouldLoadProps = getShouldLoadProps(lazyLoadRef.current)
            const shouldTriggerImpression = shouldTrigger(shouldLoadProps)
            if (shouldTriggerImpression) {
                impressionTriggered.current = true
                setTimeout(() => {
                    available()
                })
            }
        }

        addListener('scroll', detectInViewport)
        addListener('resize', detectInViewport)

        return () => {
            removeListener('scroll', detectInViewport)
            removeListener('resize', detectInViewport)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading])

    return children(lazyLoadRef) as any
}
ImpressionAvailable.displayName = 'ImpressionAvailable'
